import React, { useCallback, useState } from 'react';
import { useFormik } from 'formik';
import { authentication as actions } from '@/actions';
import { RootState, useAppSelector } from '@/state/store';

import { Button, PasswordField, Container, PhoneField } from '@/components';
import { TextField } from '@mui/material';
import { AuthenticationStatus } from '@/contracts';
import { Navigate, useNavigate } from 'react-router-dom';

import { styled } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import t from '@/utils/translate';
import useDebounceCallback from '@/utils/useDebounceCallback';
import isEmailAddressValid from '@/utils/isEmailAddressValid';

/*
const ButtonLink = styled(Button)(({ theme }) => ({
  display: 'flex',
  margin: `${theme.spacing(2.5)}px auto 0`,
  background: 'none',
  border: 'none',
  cursor: 'pointer',

  '&:focus': {
    outline: 'none',
  },
}));
*/

const Link = styled('a')(({ theme }) => ({
  cursor: 'pointer',
  color: theme.palette.primary.dark,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export default function ({
  confirmByPhoneUrl, accessRecoveryUrl
}: { confirmByPhoneUrl: string, accessRecoveryUrl: string }) {
  const nav = useNavigate();

  const { status, exitPoint } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.authentication.status,
        (state: RootState) => state.common.exitPoint,
      ],
      (status, exitPoint) => ({ status, exitPoint })
    )
  );  

  const [byPhone, setByPhone] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      phone: ''
    },
    validate: ({ phone, email, password }) => {
      const errors: Record<string, string> = {};

      email = email.trim()
      if (!byPhone) {
        if (!email) {
          errors.email = t('login.validation.email');
        } else if (!isEmailAddressValid(email)) {
          errors.email = t('login.validation.invalidEmail');
        }

        if (!password) {
          errors.password = t('login.hint.pwd');
        }
      }

      return errors;
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: ({ phone, email, password }) => {
      if (byPhone) {
        actions.login({ phone });
      } else {
        actions.login({ email: email.trim(), password });
      }
    }
  });


  const trimEmail = useCallback(() => {
    if (formik.values.email.length !== formik.values.email.trim().length)
      formik.setFieldValue('email', formik.values.email.trim())
  },[formik])

  const debouncedTrimEmail = useDebounceCallback(trimEmail, 1000)

  const onChangeEmail = useCallback((e:React.ChangeEvent) => {
    formik.handleChange(e)
    debouncedTrimEmail()
  },[formik])

  const onBlurEmail = useCallback((e:React.FocusEvent) => {
    formik.handleBlur(e)
    trimEmail()
  },[formik, trimEmail])


  if (status === AuthenticationStatus.AUTHENTICATED) {
    return <Navigate to={exitPoint || '/'} replace/>;
  }

  if (
    status === AuthenticationStatus.PENDING_CONFIRMATION ||
    status === AuthenticationStatus.CONFIRMATION
  ) {
    return <Navigate to={confirmByPhoneUrl} replace/>;
  }

  const processing = status === AuthenticationStatus.PROCESSING;

  return (
    <>
      <h3>{t('login.title.login')}</h3>
      <form onSubmit={formik.handleSubmit}>
        {byPhone && (
          <>
            <PhoneField
              {...formik.getFieldProps('phone')}
              required
              label={t('login.label.phone')}
              error={formik.touched.phone && !!formik.errors.phone}
              helperText={formik.touched.phone && formik.errors.phone}
              disabled={processing}
              fullWidth
              margin="dense"
              variant="outlined"
              autoFocus
              size="small"
            />
            <Container margin-v="medium">
              <span>
                {t('login.hint.phone', true)}
              </span>
            </Container>
          </>
        )}

        {!byPhone && (
          <>
            <Container margin-v="small">
              <TextField
                {...formik.getFieldProps('email')}
                autoComplete="email"
                onChange={onChangeEmail}
                onBlur={onBlurEmail}
                disabled={processing}
                label={t('login.label.email')}
                error={formik.touched.email && !!formik.errors.email}
                helperText={formik.touched.email && formik.errors.email}
                fullWidth
                margin="dense"
                variant="outlined"
                autoFocus
                placeholder={t('login.hint.email')}
                size="small"
              />
            </Container>
            
            <Container margin-v="small">
              <PasswordField
                {...formik.getFieldProps('password')}
                disabled={processing}
                autoComplete="current-password"
                label={t('login.label.pwd')}
                error={formik.touched.password && !!formik.errors.password}
                helperText={formik.touched.password && formik.errors.password}
                fullWidth
                margin="dense"
                variant="outlined"
                placeholder={t('login.hint.pwd')}
                size="small"
              />
              <Container margin-v="small" sx={{ textAlign: 'right' }}>
                <Link
                  color="primary"
                  onClick={(e) => nav(accessRecoveryUrl)}
                >
                  {t('authentication.hint.forgotPwd')}
                </Link>
              </Container>
            </Container>
          </>
        )}

        <Button
          type="submit"
          disabled={processing}
          variant="contained"
          fullWidth
          color="primary"
          size="large"
          loading={processing}
        >
          {byPhone ? t('action.next') : t('action.login')}
        </Button>
      </form>
      {byPhone
        && (
          <Container margin-v="medium" margin-h="small" style={{ textAlign: 'center' }}>
            <Link
              onClick={(e) => {
                e.preventDefault();
                setByPhone(false);
                formik.resetForm();
              }}
              color="primary"
            >
              {t('login.hint.loginByPwd')}
            </Link>
          </Container>
        )/*
        : (
          <ButtonLink
            type="reset"
            color="inherit"
            fullWidth
            onClick={() => {
              setByPhone(true);
              formik.resetForm();
            }}
            size="large"
            variant="contained"
          >
            {t('action.back')}
          </ButtonLink>
        )*/
      }
    </>
  );
}
